import { afterNextRender, ChangeDetectionStrategy, Component, effect, model, signal, viewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";

import { FolderModel } from "@documentation/models/folder.model";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";

enum Type {
  Document = "Document",
  Folder = "Folder"
}

@Component({
  selector: "component-create-document-or-folder-modal",
  templateUrl: "./create-document-or-folder-modal.component.html",
  styleUrl: "./create-document-or-folder-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, ModalComponent, InputComponent, FormsModule, CustomIconComponent]
})
export class CreateDocumentOrFolderModalComponent extends BaseModalComponent {
  readonly targetFolder = model<FolderModel>();

  protected readonly Type = Type;

  protected readonly control: FormControl<string> = new FormControl("", { validators: [Validators.required], nonNullable: true });

  protected readonly selectedType = signal(Type.Document);

  protected readonly inputComponent = viewChild<InputComponent>("nameInput");

  private readonly _defaultDocumentName = "New Document";
  private readonly _defaultFolderName = "New Folder";

  constructor() {
    super();

    // Pre-fill default name when type is selected
    effect(() => {
      const type = this.selectedType();
      const currentValue = this.control.value;

      if (type === Type.Document) {
        if (!currentValue || currentValue === this._defaultFolderName) {
          this.control.setValue(this._defaultDocumentName);
          this.focusAndSetCursor(this._defaultDocumentName);
        }
      } else if (type === Type.Folder) {
        if (!currentValue || currentValue === this._defaultDocumentName) {
          this.control.setValue(this._defaultFolderName);
          this.focusAndSetCursor(this._defaultFolderName);
        }
      }
    });

    // Handle initial state when modal opens
    afterNextRender(() => {
      const type = this.selectedType();
      if (!this.control.value) {
        if (type === Type.Document) {
          this.control.setValue(this._defaultDocumentName);
          this.focusAndSetCursor(this._defaultDocumentName);
        } else if (type === Type.Folder) {
          this.control.setValue(this._defaultFolderName);
          this.focusAndSetCursor(this._defaultFolderName);
        }
      }
    });
  }

  private focusAndSetCursor(text: string): void {
    // Focus and set cursor position after the text
    setTimeout(() => {
      const input = this.inputComponent();
      if (input) {
        input.focus();
        // Set cursor position at the end of the text
        setTimeout(() => {
          const inputElement = input.inputHtmlElement()?.nativeElement;
          if (inputElement) {
            const length = text.length;
            inputElement.setSelectionRange(length, length);
          }
        }, 0);
      }
    }, 0);
  }

  protected onSave(): void {
    if (this.control.valid) {
      this.activeModal.close({ name: this.control.value, type: this.selectedType().toLowerCase() });
    }
  }
}
